import {captureException} from '@sentry/browser';
import flagsmith from 'flagsmith';
import {ITraits} from 'flagsmith/types';

import {useCallback, useEffect, useRef, useState} from 'react';

import {isNil} from 'ramda';

interface UseFlagSmithProps {
  environmentID: string;
  identity?: string;
  apiUrl: string;
  skip?: boolean;
  traits?: ITraits;
}

export function useFlagsmith({environmentID, identity, apiUrl, skip, traits}: UseFlagSmithProps) {
  const [isFlagSmithInitialized, setInitialized] = useState(false);

  // This is used to check if the feature flags are loaded from the API
  // and not from the local storage cache. Cached flags may not be up to date.
  const [areFeatureFlagsLoadedFromApi, setLoadedFromApi] = useState(false);

  const handleInitFlagsmith = useCallback(
    (userIdentity?: string, newTraits?: ITraits) => {
      flagsmith
        .init({
          environmentID,
          api: apiUrl,
          identity: userIdentity,
          cacheFlags: true,
          traits: newTraits,
          onChange: (_changes, params) => {
            if (!!params.isFromServer) {
              setLoadedFromApi(true);
            }
          },
        })
        .then(() => setInitialized(true))
        .catch((error: Error) => {
          // Flagsmith sometimes throws an error with message 'No error message' without any other information
          if (error?.message !== 'No error message' || isNil(error?.message)) {
            console.error('Error when initializing Flagsmith:', error);
            captureException(error);
          }
          setInitialized(false);
        });
    },
    [environmentID, apiUrl]
  );

  useOnMount(() => {
    if (skip) {
      return;
    }
    handleInitFlagsmith(identity, traits);
  });

  return {isFlagSmithInitialized, handleInitFlagsmith, areFeatureFlagsLoadedFromApi};
}

const useOnMount = (func?: () => void) => {
  const didMountRef = useRef(false);

  useEffect(() => {
    if (didMountRef.current === false) {
      func?.();
      didMountRef.current = true;
    }

    return () => {
      didMountRef.current = false;
    };
  }, []);
};
